/**
 * Loading Spinner Component - AI Enablement Platform
 * 
 * Reusable loading spinner with different sizes and variants
 * Following the Linear-style design system from UX specification.
 */

import React from 'react';

interface LoadingSpinnerProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'primary' | 'secondary' | 'white';
  className?: string;
  fullScreen?: boolean;
  message?: string;
}

export function LoadingSpinner({ 
  size = 'md', 
  variant = 'primary',
  className = '',
  fullScreen = false,
  message
}: LoadingSpinnerProps) {
  const sizeClasses = {
    xs: 'h-3 w-3',
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-8 w-8',
    xl: 'h-12 w-12',
  };

  const variantClasses = {
    primary: 'text-brand',
    secondary: 'text-muted',
    white: 'text-white',
  };

  const spinner = (
    <div className={`${sizeClasses[size]} ${variantClasses[variant]} ${className}`}>
      <svg
        className="animate-spin h-full w-full"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    </div>
  );

  if (fullScreen) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-bg flex items-center justify-center">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            {spinner}
          </div>
          {message && (
            <p className="text-gray-500 dark:text-gray-400 text-sm">{message}</p>
          )}
        </div>
      </div>
    );
  }

  if (message) {
    return (
      <div className="flex items-center space-x-3">
        {spinner}
        <span className="text-muted text-sm">{message}</span>
      </div>
    );
  }

  return spinner;
}

export default LoadingSpinner;
